import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { ChakraProvider } from "@chakra-ui/react";
import { SkipNavLink } from "@chakra-ui/skip-nav";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider } from "@tanstack/react-query";

import jQuery from "jquery";

import Root from "@/js/components/Root";
import { queryClient } from "@/query/queryClient";
import { theme } from "@/theme/index";
import { I18nextProvider, useTranslation } from "react-i18next";

import { initDatadogRum } from "@/utils/datadogUtils";
import { initI18nApp, i18nInstance } from "@/i18n";
import { I18nLanguageSync } from "@/i18n/I18nLanguageSync";
import { addAllIconsToLibrary } from "@/utils/fontAwesomeUtils";

// normalize needs to come in first before ALL other styles
import "normalize.css";
import "@/scss/main.scss";

// Makes jQuery available in the global scope.
// I don't think this is required anymore as the only two places I can see jQuery being used are in
// authorisedApi.js and ClassroomToolMenu/index.jsx and both of these are now using the import statement.
declare global {
	interface Window {
		$: typeof jQuery;
		jQuery: typeof jQuery;
		/**
		 * Required for typesafety when calling the Chatbot API.
		 *
		 * Used in src/js/components/Chatbot/index.tsx
		 *
		 * See: https://www.chatbot.com/docsv1/chat-widget-api/#hooks
		 */
		BE_API: {
			onLoad: () => void;
			setUserAttributes: (attributes: Record<string, string>) => void;
		};
		/**
		 * Required for typesafety when calling the Chatbot API.
		 *
		 * Used in src/js/components/Chatbot/index.tsx
		 *
		 * See: https://www.chatbot.com/docsv1/chat-widget-api/#hooks
		 */
		__be: {
			id: string;
		};
	}
}
window.$ = window.jQuery = jQuery;

initDatadogRum();

addAllIconsToLibrary();

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

function AppSkipNavLink(): JSX.Element {
	const { t } = useTranslation();
	return (
		<SkipNavLink className="skip-link" tabIndex={0}>
			{t("chrome.skipNav")}
		</SkipNavLink>
	);
}

function renderApp(): void {
	root.render(
		<I18nextProvider i18n={i18nInstance}>
			<QueryClientProvider client={queryClient}>
				<I18nLanguageSync>
					<ChakraProvider theme={theme} toastOptions={{ defaultOptions: { position: "bottom-right" } }}>
						<BrowserRouter>
							<AppSkipNavLink />
							<Root />
						</BrowserRouter>
						<ReactQueryDevtools initialIsOpen={false} />
					</ChakraProvider>
				</I18nLanguageSync>
			</QueryClientProvider>
		</I18nextProvider>
	);
}

void initI18nApp()
	.then(() => {
		renderApp();
	})
	.catch((error: unknown) => {
		console.error("Failed to initialise i18n", error);
	});
